/**
 * F-148 §10.2 receipt detail (AC20–AC23): actor, time, supplier, display reference with origin
 * badge, immutable line snapshots, net accepted after corrections, the correction list, the
 * discrepancy download and a per-line correction action. No edit or delete controls exist.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, PackagePlus } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { StockReceipt, StockReceiptLine } from '@dukanti/shared/admin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { withRawValues } from '../lib/error-messages';
import { DiscrepancyDownloadButton } from './discrepancy-download-button';
import { ReferenceBadge } from './reference-badge';

export interface ReceiptDetailProps {
  readonly receipt: StockReceipt;
  readonly canCorrect: boolean;
  /** True while the loaded receipt is not a trusted base for a new correction (refreshing/errored). */
  readonly correctDisabled?: boolean;
  readonly onCorrect: (line: StockReceiptLine) => void;
  readonly onNewLinked?: () => void;
  readonly imageUrlFor?: (path: string) => string | null;
  readonly className?: string;
}

function formatDateTime(iso: string, lang: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function ReceiptDetail({
  receipt,
  canCorrect,
  correctDisabled = false,
  onCorrect,
  onNewLinked,
  className,
}: ReceiptDetailProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const correctionsByLine = new Map<string, StockReceipt['corrections']>();
  for (const correction of receipt.corrections) {
    const list = correctionsByLine.get(correction.receiptLineId) ?? [];
    list.push(correction);
    correctionsByLine.set(correction.receiptLineId, list);
  }

  return (
    <div
      className={cn('space-y-6 text-start', className)}
      data-testid="receipt-detail"
      data-receipt-id={receipt.id}
    >
      <section className="border-border bg-card shadow-admin-card rounded-lg border p-4 sm:p-5">
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground text-xs">
              {t('admin.receiving.receipt.reference')}
            </dt>
            <dd className="mt-1">
              <ReferenceBadge reference={receipt} />
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">
              {t('admin.receiving.receipt.supplier')}
            </dt>
            <dd className="text-foreground mt-1 text-sm font-medium" data-testid="receipt-supplier">
              <bdi>{receipt.supplier.nameSnapshot}</bdi>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">
              {t('admin.receiving.receipt.postedBy')}
            </dt>
            <dd className="text-foreground mt-1 text-sm" data-testid="receipt-posted-by">
              <bdi dir="ltr">{receipt.postedByEmail}</bdi>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">
              {t('admin.receiving.receipt.postedAt')}
            </dt>
            <dd className="text-foreground mt-1 text-sm" data-testid="receipt-posted-at">
              <time dateTime={receipt.postedAt}>{formatDateTime(receipt.postedAt, lang)}</time>
            </dd>
          </div>
          {receipt.linkedReceiptId ? (
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground text-xs">
                {t('admin.receiving.receipt.linked')}
              </dt>
              <dd className="mt-1 text-sm">
                <Link
                  to={`/stock/receiving/receipts/${encodeURIComponent(receipt.linkedReceiptId)}`}
                  className="text-foreground inline-flex min-h-11 items-center underline underline-offset-4"
                  data-testid="receipt-linked-link"
                >
                  <bdi dir="ltr">{receipt.linkedReceiptId.slice(0, 8)}</bdi>
                </Link>
              </dd>
            </div>
          ) : null}
        </dl>
        <p className="text-muted-foreground mt-4 text-xs" data-testid="receipt-no-edit-notice">
          {t('admin.receiving.receipt.noEditNotice')}
        </p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <DiscrepancyDownloadButton receipt={receipt} className="min-h-11" />
          {onNewLinked ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={onNewLinked}
              data-testid="receipt-new-linked"
            >
              <PackagePlus aria-hidden="true" className="size-4" />
              {t('admin.receiving.receipt.newLinked')}
            </Button>
          ) : null}
        </div>
      </section>

      <section aria-labelledby="receipt-totals-heading">
        <h2 id="receipt-totals-heading" className="sr-only">
          {t('admin.receiving.review.totals')}
        </h2>
        <dl
          className="border-border bg-admin-surface-muted grid grid-cols-2 gap-3 rounded-lg border p-4 text-sm sm:grid-cols-5"
          data-testid="receipt-totals"
        >
          <div>
            <dt className="text-muted-foreground text-xs">{t('admin.receiving.receipt.lines')}</dt>
            <dd className="font-semibold tabular-nums">
              <bdi dir="ltr">{receipt.totals.lines}</bdi>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.expected')}</dt>
            <dd className="font-semibold tabular-nums">
              <bdi dir="ltr">{receipt.totals.expected}</bdi>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.accepted')}</dt>
            <dd className="font-semibold tabular-nums">
              <bdi dir="ltr">{receipt.totals.accepted}</bdi>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.damaged')}</dt>
            <dd className="font-semibold tabular-nums">
              <bdi dir="ltr">{receipt.totals.damaged}</bdi>
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('admin.receiving.line.missing')}</dt>
            <dd className="font-semibold tabular-nums">
              <bdi dir="ltr">{receipt.totals.missing}</bdi>
            </dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="receipt-lines-heading" className="space-y-3">
        <h2 id="receipt-lines-heading" className="font-display text-foreground text-lg">
          {t('admin.receiving.receipt.lines')}
        </h2>
        <ul className="space-y-3">
          {receipt.lines.map((line) => {
            const corrections = correctionsByLine.get(line.id) ?? [];
            const net = line.acceptedUnits - line.correctedUnits;
            return (
              <li
                key={line.id}
                className="border-border bg-card shadow-admin-card space-y-3 rounded-lg border p-4"
                data-testid="receipt-line"
                data-line-id={line.id}
                data-variant-id={line.variantId}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                      {t('admin.receiving.line.lineNo', { line: line.lineNo })}
                    </p>
                    <p
                      className="text-foreground text-base font-semibold"
                      data-testid="receipt-line-product"
                    >
                      <bdi>{line.productNameSnapshot}</bdi>
                    </p>
                    <p className="text-muted-foreground text-sm" data-testid="receipt-line-variant">
                      <bdi>{line.optionSummarySnapshot || '—'}</bdi>
                    </p>
                    {line.barcodeSnapshot || line.identifiers.supplierCode ? (
                      <p className="text-muted-foreground font-mono text-xs">
                        {line.barcodeSnapshot ? <bdi dir="ltr">{line.barcodeSnapshot}</bdi> : null}
                        {line.identifiers.supplierCode ? (
                          <>
                            {line.barcodeSnapshot ? ' · ' : ''}
                            <bdi dir="ltr">{line.identifiers.supplierCode}</bdi>
                          </>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                  {canCorrect && line.correctableUnits > 0 ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11"
                      disabled={correctDisabled}
                      onClick={() => onCorrect(line)}
                      data-testid="receipt-line-correct"
                    >
                      {t('admin.receiving.correction.action')}
                    </Button>
                  ) : null}
                </div>

                <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
                  <div>
                    <dt className="text-muted-foreground text-xs">
                      {t('admin.receiving.line.expected')}
                    </dt>
                    <dd className="tabular-nums">
                      <bdi dir="ltr">{line.expectedUnits}</bdi>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs">
                      {t('admin.receiving.line.accepted')}
                    </dt>
                    <dd className="tabular-nums" data-testid="receipt-line-accepted">
                      <bdi dir="ltr">{line.acceptedUnits}</bdi>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs">
                      {t('admin.receiving.line.damaged')}
                    </dt>
                    <dd className="tabular-nums">
                      <bdi dir="ltr">{line.damagedUnits}</bdi>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs">
                      {t('admin.receiving.line.missing')}
                    </dt>
                    <dd className="tabular-nums">
                      <bdi dir="ltr">{line.missingUnits}</bdi>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground text-xs">
                      {t('admin.receiving.receipt.netAccepted')}
                    </dt>
                    <dd
                      className="font-semibold tabular-nums"
                      data-testid="receipt-line-net-accepted"
                    >
                      <bdi dir="ltr">{net}</bdi>
                      {line.correctedUnits > 0 ? (
                        <Badge variant="warning" className="ms-2">
                          <bdi dir="ltr">-{line.correctedUnits}</bdi>
                        </Badge>
                      ) : null}
                    </dd>
                  </div>
                </dl>

                {line.inventoryBefore !== null && line.inventoryAfter !== null ? (
                  <p
                    className="text-muted-foreground flex items-center gap-1 text-xs"
                    data-testid="receipt-line-inventory"
                  >
                    <span>{t('admin.barcode.result.stock')}:</span>
                    <bdi dir="ltr">{line.inventoryBefore}</bdi>
                    <ArrowRight aria-hidden="true" className="size-3 rtl:rotate-180" />
                    <bdi dir="ltr">{line.inventoryAfter}</bdi>
                  </p>
                ) : null}

                {line.unitKind === 'pack' &&
                line.packsExpected !== null &&
                line.unitsPerPack !== null ? (
                  <p className="text-muted-foreground text-xs">
                    {/* Localized sentence: `<bdi>` (dir=auto) follows the text's own direction. */}
                    <bdi>
                      {t('admin.receiving.line.packMath', {
                        packs: line.packsExpected,
                        unitsPerPack: line.unitsPerPack,
                        total: line.expectedUnits,
                      })}
                    </bdi>
                  </p>
                ) : null}

                {line.note ? (
                  <p className="text-muted-foreground text-xs">
                    <bdi>{line.note}</bdi>
                  </p>
                ) : null}

                {corrections.length > 0 ? (
                  <ul
                    className="border-border space-y-1 border-t pt-3"
                    data-testid="receipt-line-corrections"
                  >
                    {corrections.map((correction) => (
                      <li
                        key={correction.id}
                        className="text-xs"
                        data-correction-id={correction.id}
                      >
                        <bdi>
                          {t(
                            'admin.receiving.receipt.correctedBy',
                            withRawValues({
                              units: correction.units,
                              reclassify: t(
                                `admin.receiving.correction.reclassify.${correction.reclassifyAs}`,
                              ),
                              actor: correction.createdByEmail,
                              date: formatDateTime(correction.createdAt, lang),
                              reason: t(`admin.receiving.correction.reasons.${correction.reason}`),
                            }),
                          )}
                        </bdi>
                        {correction.note ? (
                          <>
                            {' — '}
                            <bdi>{correction.note}</bdi>
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-labelledby="receipt-corrections-heading" className="space-y-2">
        <h2 id="receipt-corrections-heading" className="font-display text-foreground text-lg">
          {t('admin.receiving.receipt.corrections')}
        </h2>
        {receipt.corrections.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="receipt-no-corrections">
            {t('admin.receiving.receipt.noCorrections')}
          </p>
        ) : (
          <p className="text-muted-foreground text-sm">
            <bdi dir="ltr">{receipt.corrections.length}</bdi>
          </p>
        )}
      </section>
    </div>
  );
}
